import React, { useState, useEffect } from "react";
import useApi from "@/hooks/useApi";

interface DeployLog {
  id: number;
  pipeline: string;
  environment: string;
  status: string;
  runNumber: number;
  commitSha: string;
  commitSubject: string;
  changedAreas: string | null;
  smokeResults: string | null;
  runUrl: string | null;
  deployedAt: string;
}

const PIPELINE_COLORS: Record<string, string> = {
  admin:       "bg-blue-900 text-blue-200 border-blue-700",
  messaging:   "bg-purple-900 text-purple-200 border-purple-700",
  livefluence: "bg-orange-900 text-orange-200 border-orange-700",
  landing:     "bg-green-900 text-green-200 border-green-700",
};

const STATUS_STYLES: Record<string, { bar: string; badge: string; icon: string }> = {
  success:   { bar: "border-l-4 border-green-500",  badge: "bg-green-900 text-green-200",  icon: "✅" },
  failure:   { bar: "border-l-4 border-red-500",    badge: "bg-red-900 text-red-200",      icon: "❌" },
  cancelled: { bar: "border-l-4 border-slate-500",  badge: "bg-slate-700 text-slate-300",  icon: "⏭️" },
};

function pipelineLabel(p: string) {
  return p.charAt(0).toUpperCase() + p.slice(1);
}

function relativeTime(iso: string) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60)   return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  return `${Math.round(diff / 86400)}d ago`;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    hour12: true, timeZoneName: "short",
  });
}

export default function DeployHistory() {
  const api = useApi();
  const [logs, setLogs] = useState<DeployLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>("all");

  useEffect(() => {
    api.admin.get("DeployLogs?limit=200")
      .then(r => r.json() as Promise<DeployLog[]>)
      .then(data => { setLogs(Array.isArray(data) ? data : []); })
      .catch(err => setError(`Failed to load: ${err instanceof Response ? err.status : String(err)}`))
      .finally(() => setLoading(false));
  }, []);

  const pipelines = ["all", ...Array.from(new Set(logs.map(l => l.pipeline))).sort()];
  const filtered = filter === "all" ? logs : logs.filter(l => l.pipeline === filter);

  const lastByPipeline = Object.fromEntries(
    Array.from(new Set(logs.map(l => l.pipeline))).map(p => {
      const latest = logs.find(l => l.pipeline === p);
      return [p, latest];
    })
  );

  return (
    <div className="min-h-screen bg-slate-950 text-white p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Deploy History</h1>
          <p className="text-slate-400 text-sm mt-1">All pipelines · staging only · last 200 deploys</p>
        </div>

        {/* Latest per pipeline */}
        {!loading && logs.length > 0 && (
          <div className="grid grid-cols-2 gap-3 mb-6">
            {Object.entries(lastByPipeline).map(([pipeline, log]) => {
              if (!log) return null;
              const s = STATUS_STYLES[log.status] ?? STATUS_STYLES.cancelled;
              return (
                <div key={pipeline} className={`bg-slate-800 rounded-lg p-3 ${s.bar}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-slate-300 uppercase tracking-wide">{pipelineLabel(pipeline)}</span>
                    <span className={`text-xs px-2 py-0.5 rounded font-medium ${s.badge}`}>{s.icon} {log.status}</span>
                  </div>
                  <div className="text-sm text-white truncate">{log.commitSubject}</div>
                  <div className="text-xs text-slate-400 mt-1">{relativeTime(log.deployedAt)} · #{log.runNumber}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2 mb-4 flex-wrap">
          {pipelines.map(p => (
            <button key={p} onClick={() => setFilter(p)}
              className={`px-3 py-1 rounded text-sm font-medium transition-colors ${filter === p ? "bg-blue-600 text-white" : "bg-slate-800 text-slate-300 hover:bg-slate-700"}`}>
              {pipelineLabel(p)}
            </button>
          ))}
        </div>

        {loading && <div className="text-slate-400 py-8 text-center">Loading...</div>}
        {error && <div className="bg-red-900/30 border border-red-700 rounded p-3 text-red-300 text-sm">{error}</div>}

        {!loading && !error && filtered.length === 0 && (
          <div className="text-slate-500 py-8 text-center">No deploy logs yet.</div>
        )}

        <div className="space-y-2">
          {filtered.map(log => {
            const s = STATUS_STYLES[log.status] ?? STATUS_STYLES.cancelled;
            const pColor = PIPELINE_COLORS[log.pipeline] ?? "bg-slate-700 text-slate-200 border-slate-600";
            return (
              <div key={log.id} className={`bg-slate-800 rounded-lg p-4 ${s.bar}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded border font-medium ${pColor}`}>{pipelineLabel(log.pipeline)}</span>
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${s.badge}`}>{s.icon} {log.status}</span>
                      <span className="text-xs text-slate-500">#{log.runNumber}</span>
                      <span className="text-xs font-mono text-slate-500">{log.commitSha.slice(0, 7)}</span>
                      {log.changedAreas && <span className="text-xs text-slate-400">{log.changedAreas}</span>}
                    </div>
                    <div className="text-sm text-white">{log.commitSubject}</div>
                    <div className="flex items-center gap-3 mt-1">
                      <span className="text-xs text-slate-400" title={formatDate(log.deployedAt)}>{relativeTime(log.deployedAt)} · {formatDate(log.deployedAt)}</span>
                      {log.smokeResults && (
                        <span className={`text-xs ${log.smokeResults.includes("fail") ? "text-red-400" : "text-green-400"}`}>
                          smoke: {log.smokeResults}
                        </span>
                      )}
                    </div>
                  </div>
                  {log.runUrl && (
                    <a href={log.runUrl} target="_blank" rel="noreferrer"
                      className="text-xs text-blue-400 hover:text-blue-300 whitespace-nowrap shrink-0">
                      CI run →
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
